<template>
  <div class="racquet-sports-container">
    <div
      class="match"
      v-for="match in matches"
      :key="match.id"
    >
      <div class="status" :class="getStatusClass(match.status)">
        {{ match.status }}
      </div>

      <div
        class="player-row"
        v-for="(participant, playerIndex) in match.participants"
        :key="playerIndex"
        :class="{ 'winner': match.result?.winner === playerIndex }"
      >
        <div class="player-name">
          <div
            v-for="(name, nameIndex) in getParticipantNames(participant)"
            :key="nameIndex"
            class="name-line"
          >
            {{ name }}
          </div>
        </div>
        <div class="scores">
          <span class="sets">{{ participant.totalSetsWon || 0 }}</span>
          <div class="set-scores">
            <span
              v-for="(score, setIndex) in participant.setScores"
              :key="setIndex"
              class="set-score"
              :class="getSetWinnerClass(participant, match.participants[playerIndex === 0 ? 1 : 0], setIndex)"
            >
              {{ formatSetScore(participant, match.participants[playerIndex === 0 ? 1 : 0], setIndex) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { timeOutline, trophyOutline, checkmarkCircleOutline } from 'ionicons/icons';
import moment from 'moment';

export default defineComponent({
  name: 'RacquetSportsResults',
  props: {
    matches: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  setup() {
    return {
      timeOutline,
      trophyOutline,
      checkmarkCircleOutline
    };
  },
  methods: {
    getParticipantNames(participant) {
      console.log('participant', participant);
      if (participant.member1 && participant.member1.name1) {
        return [participant.member1.name1];
      }

      if (participant.name1 && participant.name2) {
        return [participant.name1, participant.name2];
      }

      if (participant.member1 && participant.member1.profile && participant.member2 && participant.member2.profile) {
        return [`${participant.member1.profile.firstName} ${participant.member1.profile.lastName}`, `${participant.member2.profile.firstName} ${participant.member2.profile.lastName}`];
      }


      if (participant.member) {
        if (participant.member.profile.firstName && participant.member.profile.lastName) {
          return [`${participant.member.profile.firstName} ${participant.member.profile.lastName}`];
        }
        return [participant.member.name || participant.member.name1 || 'Unknown Member'];
      }

      if (participant.members && participant.members.length > 0) {
        return participant.members.map(member => {
          if (member.profile && member.profile.firstName && member.profile.lastName) {
            return `${member.profile.firstName} ${member.profile.lastName}`;
          }
          return member.name || member.name1 || 'Unknown';
        });
      }

      if (participant.name1) {
        return [participant.name1];
      }

      return [participant.name || 'Unknown'];
    },

    getParticipantName(participant) {
      // Keep this method for backwards compatibility in result text
      const names = this.getParticipantNames(participant);
      return names.join(' / ');
    },

    getSetHeaders(match) {
      if (!match.participants || !match.participants[0]?.setScores) return [];
      return match.participants[0].setScores;
    },

    getStatusClass(status) {
      const statusLower = (status || '').toLowerCase();
      if (statusLower.includes('completed') || statusLower.includes('finished')) return 'completed';
      if (statusLower.includes('progress') || statusLower.includes('live')) return 'live';
      if (statusLower.includes('not started') || statusLower.includes('scheduled')) return 'scheduled';
      return 'default';
    },

    getSetWinnerClass(participant, opponent, setIndex) {
      const participantScore = parseInt(participant.setScores?.[setIndex]) || 0;
      const opponentScore = parseInt(opponent.setScores?.[setIndex]) || 0;
      if (participantScore > opponentScore) return 'set-winner';
      if (participantScore < opponentScore) return 'set-loser';
      return '';
    },

    getResultText(match) {
      if (match.result?.score) return `Final Score: ${match.result.score}`;
      if (match.result?.sets) return `Sets Won: ${match.result.sets}`;
      const winner = match.participants?.[match.result?.winner];
      if (winner) return `Winner: ${this.getParticipantName(winner)}`;
      return 'Match Completed';
    },

    formatDateTime(timestamp) {
      return moment(parseInt(timestamp)).format('MMM DD, YYYY HH:mm');
    },

    formatSetScore(participant, opponent, setIndex) {
      const participantScore = participant.setScores?.[setIndex];
      const opponentScore = opponent.setScores?.[setIndex];

      if (!participantScore && participantScore !== 0) return '-';

      // Check if there's a tiebreak score for this set
      const participantTiebreak = participant.tiebreakScores?.[setIndex];
      const opponentTiebreak = opponent.tiebreakScores?.[setIndex];

      if (participantTiebreak !== null && participantTiebreak !== undefined &&
          opponentTiebreak !== null && opponentTiebreak !== undefined) {
        // Show tiebreak score in parentheses
        return `${participantScore}(${participantTiebreak})`;
      }

      return participantScore.toString();
    }
  }
});
</script>

<style lang="scss" scoped>
.racquet-sports-container {
  padding: 1rem;
  background: #fafafa;
  border-radius: 12px;
}

.match {
  margin-bottom: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease-in-out;

  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
    transform: translateY(-1px);
  }

  &:last-child {
    margin-bottom: 0;
  }
}

.status {
  font-size: 0.7rem;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  margin-bottom: 0.75rem;
  display: inline-block;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;

  &.completed {
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
    box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
  }

  &.live {
    background: linear-gradient(135deg, #f59e0b, #d97706);
    color: white;
    box-shadow: 0 2px 4px rgba(245, 158, 11, 0.2);
  }

  &.scheduled {
    background: linear-gradient(135deg, #6b7280, #4b5563);
    color: white;
    box-shadow: 0 2px 4px rgba(107, 114, 128, 0.2);
  }

  &.default {
    background: linear-gradient(135deg, #6b7280, #4b5563);
    color: white;
    box-shadow: 0 2px 4px rgba(107, 114, 128, 0.2);
  }
}

.player-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-radius: 6px;
  transition: background-color 0.15s ease-in-out;

  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }

  &.winner {
    background: linear-gradient(90deg, rgba(16, 185, 129, 0.05), rgba(16, 185, 129, 0.01));

    .player-name {
      font-weight: 600;
      color: #065f46;
    }

    .sets {
      color: #059669;
    }
  }
}

.player-name {
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
  transition: color 0.15s ease-in-out;
}

.name-line {
  line-height: 1.2;

  &:not(:last-child) {
    margin-bottom: 0.125rem;
  }
}

.scores {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.sets {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  min-width: 20px;
  text-align: center;
  background: linear-gradient(135deg, #f3f4f6, #e5e7eb);
  border-radius: 6px;
  padding: 0.25rem 0.5rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.set-scores {
  display: flex;
  gap: 0.375rem;
}

.set-score {
  font-size: 0.875rem;
  color: #6b7280;
  min-width: 28px;
  text-align: center;
  padding: 0.25rem 0.375rem;
  border-radius: 4px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  transition: all 0.15s ease-in-out;

  &.set-winner {
    color: #1f2937;
    font-weight: 600;
    background: linear-gradient(135deg, #dbeafe, #bfdbfe);
    border-color: #93c5fd;
    box-shadow: 0 1px 2px rgba(59, 130, 246, 0.1);
  }

  &.set-loser {
    color: #9ca3af;
    background: #f3f4f6;
    border-color: #d1d5db;
  }
}

@media (max-width: 768px) {
  .racquet-sports-container {
    padding: 0.75rem;
  }

  .match {
    padding: 0.75rem;
  }

  .player-name {
    font-size: 0.8rem;
  }

  .sets {
    font-size: 1rem;
    min-width: 18px;
    padding: 0.2rem 0.4rem;
  }

  .set-score {
    font-size: 0.75rem;
    min-width: 24px;
    padding: 0.2rem 0.3rem;
  }

  .scores {
    gap: 0.75rem;
  }

  .set-scores {
    gap: 0.25rem;
  }
}
</style>